import { useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/authApi';
import { useAuthStore } from '../store/authStore';
import { handleSessionExpired } from '@shared/utils/authSession';
import { isTokenExpired } from '@shared/utils/jwt';
import { useInstallLanguage } from '../hooks/useInstallLanguage';
import { Spin } from 'antd';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { setUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const { t } = useTranslation(['auth']);

  const hasToken = !!localStorage.getItem('access_token');
  const token = localStorage.getItem('access_token');

  // Проверить истечение токена при старте
  useEffect(() => {
    if (token && isTokenExpired(token)) {
      // Токен истек, обработать истечение сессии
      handleSessionExpired('expired', {
        messageApi: message,
        t,
        showNotification: true,
        redirectToLogin: true,
      });
      return;
    }
  }, [token, message, t]);

  // Попытаться загрузить текущего пользователя при старте
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['currentUser'],
    queryFn: authApi.getCurrentUser,
    enabled: hasToken && !isTokenExpired(token || ''),
    retry: false,
  });

  // Язык интерфейса установки (`MAINLANG`) — применяется, только если оператор не выбрал
  // язык сам. Здесь, а не в `config.ts`: ручка требует авторизации.
  useInstallLanguage(Boolean(user));

  // Обновить store когда получен пользователь
  useEffect(() => {
    if (user) {
      setUser(user);
    } else if (error) {
      // Если не удалось загрузить пользователя - обработать истечение сессии
      // Но только если это не страница логина (чтобы избежать бесконечного редиректа)
      if (location.pathname !== '/login') {
        handleSessionExpired('unauthorized', {
          messageApi: message,
          t,
          showNotification: true,
          redirectToLogin: true,
        });
      } else {
        // На странице логина просто очистить токены
        const { logout } = useAuthStore.getState();
        logout();
      }
    }
  }, [user, error, setUser, location.pathname, message, t]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
}

